import { get, ref } from "firebase/database";
import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { HIST_CSS, Legend, Marker, MarkerTip, STAGE, Tip, hhmm } from "../components/historyChart";
import { db } from "../firebase";
import {
  PANEL_RAILED_MV,
  Reboot,
  Row,
  counterRises,
  dayList,
  dayStart,
  downsample,
  findGaps,
  findReboots,
  parseDay,
  stageSegments,
  ticksFor,
} from "../lib/history";

/**
 * Read-only view of /history/wifi_switch/<day>, written by the board itself.
 * One `get()` per day, never a listener: /history is not a source and a
 * listener on its parent would download all seven days.
 */

const MAX_POINTS = 1500;
const MIN_ZOOM_S = 30;
const DUTY_MAX = 1023;

const RESET: Record<number, string> = {
  0: "power-on",
  1: "HW WDT",
  2: "exception",
  3: "SW WDT",
  4: "soft restart/OTA",
  5: "deep-sleep wake",
  6: "external reset",
};
const CRASH: Record<number, string> = {
  2: "exception",
  3: "soft WDT",
  253: "stack smash",
  254: "abort/panic",
};

/** Lane order, top to bottom. */
const LANES = [
  { key: "reboot", label: "reboot", shape: "triangle", color: "var(--c5)" },
  { key: "b", label: "BMS cut", shape: "diamond", color: "var(--c2)" },
  { key: "r", label: "VBATT railed", shape: "square", color: "var(--c4)" },
  { key: "e", label: "publish gagal", shape: "cross", color: "var(--c1)" },
  { key: "om", label: "heap gagal", shape: "circle", color: "var(--c3)" },
] as const;

interface Point {
  t: number;
  v?: number;
  s?: number;
  ma?: number;
  pos?: number;
  neg?: number;
  o?: number;
  d?: number;
  c?: number;
  h?: number;
  k?: number;
  [temp: string]: number | undefined;
}

const pct = (x?: number) => (x === undefined ? undefined : (x * 100) / DUTY_MAX);
const scale = (x: number | undefined, by: number) => (x === undefined ? undefined : x / by);

function toPoint(r: Row): Point {
  const p: Point = {
    t: r.t,
    v: scale(r.v, 1000),
    s: scale(r.s, 1000),
    ma: scale(r.ma, 1000),
    o: pct(r.o),
    d: pct(r.d),
    c: pct(r.c),
    h: r.h,
    k: r.k,
    ...r.temps,
  };
  if (p.ma !== undefined) {
    p.pos = Math.max(p.ma, 0);
    p.neg = Math.min(p.ma, 0);
  }
  return p;
}

export default function Charger() {
  const days = React.useMemo(() => dayList(Date.now()), []);
  const [day, setDay] = React.useState(days[0]);
  const [rows, setRows] = React.useState<Row[]>([]);
  const [status, setStatus] = React.useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = React.useState("");
  const [zoom, setZoom] = React.useState<[number, number] | null>(null);
  const [sel, setSel] = React.useState<{ a: number; b?: number } | null>(null);
  const [showMore, setShowMore] = React.useState(false);
  const req = React.useRef(0);

  const load = React.useCallback((d: string) => {
    const me = ++req.current; // a slow earlier day must not overwrite a newer pick
    setStatus("loading");
    get(ref(db, "history/wifi_switch/" + d))
      .then((ss) => {
        if (me !== req.current) return;
        setRows(parseDay(ss.val()));
        setStatus("ok");
      })
      .catch((err) => {
        if (me !== req.current) return;
        setRows([]);
        setError(String(err?.message ?? err));
        setStatus("error");
      });
  }, []);

  React.useEffect(() => {
    setZoom(null);
    setSel(null);
    load(day);
  }, [day, load]);

  const a = React.useMemo(() => {
    const gaps = findGaps(rows);
    const reboots = findReboots(rows);
    const rises = counterRises(rows);
    const idxOfT = new Map(rows.map((r, i) => [r.t, i]));
    return {
      gaps,
      reboots,
      segs: stageSegments(rows, gaps),
      rises,
      risesAt: rises.map((r) => idxOfT.get(r.t) as number),
      tempKeys: Array.from(new Set(rows.flatMap((r) => Object.keys(r.temps)))).sort(),
    };
  }, [rows]);

  const full: [number, number] = React.useMemo(() => {
    const t0 = dayStart(day);
    return [t0, t0 + 86400];
  }, [day]);
  const [d0, d1] = zoom ?? full;

  // Downsample only what is on screen, so zooming in recovers the detail.
  const points = React.useMemo(() => {
    const lo = rows.findIndex((r) => r.t >= d0);
    if (lo < 0) return [];
    let hi = rows.length - 1;
    while (hi > lo && rows[hi].t > d1) hi--;
    const view = rows.slice(lo, hi + 1);
    const gapSet = new Set(a.gaps.map((g) => g - lo));
    const force = new Set<number>();
    gapSet.forEach((g) => {
      force.add(g);
      force.add(g - 1);
    });
    a.reboots.forEach((r) => force.add(r.index - lo));
    a.risesAt.forEach((i) => force.add(i - lo));
    const out: Point[] = [];
    let prevT = 0;
    for (const i of downsample(view.length, MAX_POINTS, force)) {
      // A bare point makes the line (and area) break instead of bridging a gap.
      if (gapSet.has(i) && prevT && view[i].t - prevT > 1) out.push({ t: prevT + 1 });
      out.push(toPoint(view[i]));
      prevT = view[i].t;
    }
    return out;
  }, [rows, a, d0, d1]);

  const markers = React.useMemo(() => {
    const out: Record<string, Marker[]> = {};
    const add = (key: string, m: Marker) => (out[key] = [...(out[key] ?? []), m]);
    a.reboots.forEach((rb: Reboot) => {
      const lines = [`reset reason: ${rb.rr === undefined ? "?" : RESET[rb.rr] ?? rb.rr}`];
      if (rb.crash)
        lines.push(
          `crash: ${CRASH[rb.crash.xr as number] ?? rb.crash.xr}`,
          `exccause ${rb.crash.xc} · epc1 0x${rb.crash.xp} · excvaddr 0x${rb.crash.xv}`,
          `stack ${rb.crash.xs}`
        );
      add("reboot", { t: rb.t, y: 4, title: "reboot", lines });
    });
    LANES.slice(1).forEach((l, i) =>
      a.rises
        .filter((r) => r.key === l.key)
        .forEach((r) => add(l.key, { t: r.t, y: 3 - i, title: l.label, lines: [`+${r.delta}`] }))
    );
    return out;
  }, [a]);

  const inView = (t: number) => t >= d0 && t <= d1;
  const xAxis = (last: boolean) => (
    <XAxis
      type="number"
      dataKey="t"
      domain={[d0, d1]}
      allowDataOverflow
      ticks={ticksFor(d0, d1)}
      tickFormatter={hhmm}
      tick={last ? undefined : false}
      height={last ? 22 : 4}
      stroke="var(--grid)"
    />
  );
  const grid = <CartesianGrid stroke="var(--grid)" vertical={false} />;
  const rebootLines = a.reboots
    .filter((r) => inView(r.t))
    .map((r) => (
      <ReferenceLine
        key={r.t}
        x={r.t}
        stroke="var(--c5)"
        strokeDasharray="3 3"
        strokeOpacity={0.7}
      />
    ));

  const drag = {
    syncId: "charger",
    syncMethod: "value" as const,
    onMouseDown: (e: any) => e?.activeLabel != null && setSel({ a: Number(e.activeLabel) }),
    onMouseMove: (e: any) =>
      sel && e?.activeLabel != null && setSel({ a: sel.a, b: Number(e.activeLabel) }),
    onMouseUp: () => {
      if (sel?.b != null && Math.abs(sel.b - sel.a) >= MIN_ZOOM_S)
        setZoom([Math.min(sel.a, sel.b), Math.max(sel.a, sel.b)]);
      setSel(null);
    },
  };
  const selArea =
    sel?.b != null ? (
      <ReferenceArea x1={sel.a} x2={sel.b} fill="currentColor" fillOpacity={0.12} />
    ) : null;
  const margin = { top: 6, right: 8, bottom: 0, left: 0 };
  const axisTick = { fontSize: 11 };

  const emptyMsg =
    status === "loading"
      ? "Memuat…"
      : status === "error"
      ? null
      : rows.length === 0
      ? "Tidak ada rekaman untuk hari ini."
      : null;

  return (
    <div className="hist px-2 pb-10">
      <style>{HIST_CSS}</style>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h1 className="text-lg font-bold mr-2">Charger surya</h1>
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="rounded border border-slate-500/50 bg-transparent px-2 py-1 text-sm"
        >
          {days.map((d) => (
            <option key={d} value={d} className="text-black">
              {d.slice(6)}/{d.slice(4, 6)}/{d.slice(0, 4)}
              {d === days[0] ? " (hari ini)" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => load(day)}
          className="rounded border border-slate-500/50 px-2 py-1 text-sm hover:bg-slate-500/20"
        >
          <i className="fas fa-rotate mr-1" />
          Refresh
        </button>
        {zoom && (
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="rounded border border-slate-500/50 px-2 py-1 text-sm hover:bg-slate-500/20"
          >
            Reset zoom ({hhmm(zoom[0])}–{hhmm(zoom[1])})
          </button>
        )}
        <span className="text-xs opacity-60">
          {status === "ok" && `${rows.length} baris · seret pada chart untuk zoom · WIB`}
        </span>
      </div>

      {status === "error" && (
        <div className="rounded border border-red-500/50 bg-red-500/10 p-3 text-sm">
          Gagal membaca <code>/history/wifi_switch/{day}</code>: {error}
          {/permission/i.test(error) && (
            <div className="mt-1 opacity-80">
              Rule RTDB untuk <code>/history</code> belum mengizinkan baca. Rule diatur di
              Firebase console, bukan di repo ini.
            </div>
          )}
        </div>
      )}
      {emptyMsg && <div className="py-10 text-center opacity-70">{emptyMsg}</div>}

      {status === "ok" && rows.length > 0 && (
        <div className="select-none">
          <Legend
            items={[
              ["var(--c1)", "Pack (istirahat)"],
              ["var(--c2)", "Panel"],
              ...[0, 1, 2, 3].map((g) => [STAGE[g].color, STAGE[g].name] as [string, string]),
            ]}
          />
          <div style={{ height: 190 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit=" V" domain={["auto", "auto"]} />
                {a.segs.map((s) => (
                  <ReferenceArea
                    key={s.start}
                    x1={s.start}
                    x2={s.end}
                    fill={STAGE[s.g]?.color ?? "#888"}
                    fillOpacity={0.16}
                    ifOverflow="hidden"
                  />
                ))}
                {rebootLines}
                <Line dataKey="v" stroke="var(--c1)" dot={false} strokeWidth={2} isAnimationActive={false} />
                <Line dataKey="s" stroke="var(--c2)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <ReferenceLine
                  y={PANEL_RAILED_MV / 1000}
                  stroke="var(--c2)"
                  strokeDasharray="2 4"
                  strokeOpacity={0.5}
                  ifOverflow="hidden"
                />
                {selArea}
                <Tooltip
                  isAnimationActive={false}
                  content={<Tip fmt={{ v: ["Pack", "V", 2], s: ["Panel", "V", 2] }} />}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Garis putus-putus oranye = batas ADC panel ({(PANEL_RAILED_MV / 1000).toFixed(2)} V): di
            sana nilainya terpotong, bukan hasil ukur.
          </div>

          <Legend items={[["var(--c3)", "Mengisi (+)"], ["var(--c2)", "Dipakai (−)"]]} />
          <div style={{ height: 150 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit=" A" />
                <Area dataKey="pos" stroke="none" fill="var(--c3)" fillOpacity={0.45} isAnimationActive={false} />
                <Area dataKey="neg" stroke="none" fill="var(--c2)" fillOpacity={0.45} isAnimationActive={false} />
                <Line dataKey="ma" stroke="currentColor" strokeOpacity={0.7} dot={false} strokeWidth={1} isAnimationActive={false} />
                <ReferenceLine y={0} stroke="currentColor" strokeWidth={1.5} />
                {rebootLines}
                {selArea}
                <Tooltip isAnimationActive={false} content={<Tip fmt={{ ma: ["Arus", "A", 3] }} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <Legend
            items={[
              ["var(--c1)", "o: ke pin"],
              ["var(--c2)", "d: integrator"],
              ["var(--c3)", "c: plafon"],
            ]}
          />
          <div style={{ height: 150 }}>
            <ResponsiveContainer>
              <ComposedChart data={points} margin={margin} {...drag}>
                {grid}
                {xAxis(false)}
                <YAxis width={44} tick={axisTick} stroke="var(--grid)" unit="%" domain={[0, 100]} />
                <Line dataKey="c" stroke="var(--c3)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line dataKey="d" stroke="var(--c2)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line dataKey="o" stroke="var(--c1)" dot={false} strokeWidth={2} isAnimationActive={false} />
                {rebootLines}
                {selArea}
                <Tooltip
                  isAnimationActive={false}
                  content={
                    <Tip
                      fmt={{
                        o: ["out", "%", 1],
                        d: ["duty", "%", 1],
                        c: ["plafon", "%", 1],
                      }}
                    />
                  }
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <Legend items={LANES.map((l) => [l.color, l.label] as [string, string])} />
          <div style={{ height: 130 }}>
            <ResponsiveContainer>
              <ComposedChart margin={{ ...margin, left: 0 }} {...drag}>
                {grid}
                {xAxis(true)}
                <YAxis
                  type="number"
                  dataKey="y"
                  width={44}
                  domain={[-0.6, 4.6]}
                  ticks={[0, 1, 2, 3, 4]}
                  tickFormatter={(y) => LANES[4 - y]?.label.split(" ")[0] ?? ""}
                  tick={{ fontSize: 10 }}
                  stroke="var(--grid)"
                  allowDataOverflow
                />
                <ZAxis range={[70, 70]} />
                {LANES.map((l) => (
                  <Scatter
                    key={l.key}
                    data={(markers[l.key] ?? []).filter((m) => inView(m.t))}
                    dataKey="y"
                    shape={l.shape}
                    fill={l.color}
                    isAnimationActive={false}
                  />
                ))}
                {rebootLines}
                {selArea}
                <Tooltip isAnimationActive={false} content={<MarkerTip />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs opacity-60 px-1 mb-3">
            Penanda b/r/e/om muncul saat penghitung naik (dihitung per boot); garis merah putus-putus =
            reboot. Celah &gt; 2,5× jarak baris memutus garis.
          </div>

          <button
            type="button"
            className="text-sm underline opacity-80 mb-2"
            onClick={() => setShowMore((s) => !s)}
          >
            {showMore ? "Sembunyikan" : "Tampilkan"} sekunder (suhu, heap, blank)
          </button>
          {showMore && (
            <>
              {a.tempKeys.length > 0 && (
                <SmallPanel
                  title="Suhu (°C)"
                  keys={a.tempKeys}
                  unit="°C"
                  digits={1}
                  {...{ points, margin, drag, grid, xAxis, rebootLines, selArea }}
                />
              )}
              <SmallPanel title="Heap bebas minimum (B)" keys={["h"]} unit="B" digits={0} {...{ points, margin, drag, grid, xAxis, rebootLines, selArea }} />
              <SmallPanel title="blank_ms maksimum" keys={["k"]} unit="ms" digits={0} {...{ points, margin, drag, grid, xAxis, rebootLines, selArea }} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

const SMALL_COLORS = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)"];

function SmallPanel({
  title,
  keys,
  unit,
  digits,
  points,
  margin,
  drag,
  grid,
  xAxis,
  rebootLines,
  selArea,
}: {
  title: string;
  keys: string[];
  unit: string;
  digits: number;
  points: Point[];
  margin: object;
  drag: object;
  grid: React.ReactNode;
  xAxis: (last: boolean) => React.ReactNode;
  rebootLines: React.ReactNode;
  selArea: React.ReactNode;
}) {
  const fmt: Record<string, [string, string, number]> = {};
  keys.forEach((k) => (fmt[k] = [k, unit, digits]));
  return (
    <div className="mb-2">
      <div className="text-xs opacity-80 px-1">
        {title}
        {keys.length > 1 && (
          <span className="ml-3">
            {keys.slice(0, 4).map((k, i) => (
              <span key={k} className="mr-2">
                <span
                  className="inline-block w-3 mr-1 align-middle"
                  style={{ background: SMALL_COLORS[i], height: 3 }}
                />
                {k}
              </span>
            ))}
          </span>
        )}
      </div>
      <div style={{ height: 110 }}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={margin} {...drag}>
            {grid}
            {xAxis(false)}
            <YAxis width={44} tick={{ fontSize: 11 }} stroke="var(--grid)" domain={["auto", "auto"]} />
            {keys.slice(0, 4).map((k, i) => (
              <Line key={k} dataKey={k} stroke={SMALL_COLORS[i]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
            ))}
            {rebootLines}
            {selArea}
            <Tooltip isAnimationActive={false} content={<Tip fmt={fmt} />} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
